import PropTypes from "prop-types"
import { useDropzone } from "react-dropzone"
// @mui
import { styled } from "@mui/material/styles"
import { Box, Stack, Typography } from "@mui/material"
//
import Image from "minimals-template/components/Image"
import Iconify from "minimals-template/components/Iconify"
import RejectionFiles from "minimals-template/components/upload/RejectionFiles"
import BlockContent from "minimals-template/components/upload/BlockContent"

// ----------------------------------------------------------------------

export const DropZoneStyle = styled("div")(({ theme }) => ({
    outline: "none",
    overflow: "hidden",
    position: "relative",
    padding: theme.spacing(5, 1),
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create("padding"),
    backgroundColor: theme.palette.background.neutral,
    border: `1px dashed ${theme.palette.grey[500_32]}`,
    "&:hover": { opacity: 0.72, cursor: "pointer" },
}))

// ----------------------------------------------------------------------

UploadSingleFile.propTypes = {
    error: PropTypes.bool,
    file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    helperText: PropTypes.node,
    sx: PropTypes.object,
    header: PropTypes.string,
    message: PropTypes.string,
}

export default function UploadSingleFile({ error = false, file, helperText, sx, header, message, ...other }) {
    const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
        multiple: false,
        ...other,
    })

    const isImage =
        file &&
        (typeof file === "string"
            ? file.startsWith("data:image") || file.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i)
            : file.type?.startsWith("image/"))

    return (
        <Box sx={{ width: "100%", ...sx }}>
            <DropZoneStyle
                {...getRootProps()}
                sx={{
                    ...(isDragActive && { opacity: 0.72 }),
                    ...((isDragReject || error) && {
                        color: "error.main",
                        borderColor: "error.light",
                        bgcolor: "error.lighter",
                    }),
                    ...(file && {
                        padding: "12% 0",
                    }),
                }}
            >
                <input {...getInputProps()} />

                <BlockContent header={header} message={message} />

                {file && (
                    <Box
                        sx={{
                            top: 8,
                            left: 8,
                            borderRadius: 1,
                            position: "absolute",
                            width: "calc(100% - 16px)",
                            height: "calc(100% - 16px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "background.neutral",
                        }}
                    >
                        {isImage ? (
                            <Image
                                alt="file preview"
                                src={typeof file === "string" ? file : file.preview}
                                sx={{
                                    width: 1,
                                    height: 1,
                                    borderRadius: 1,
                                }}
                            />
                        ) : (
                            <Stack spacing={1} alignItems="center">
                                <Iconify icon="eva:file-fill" sx={{ width: 48, height: 48, color: "text.secondary" }} />
                                <Typography variant="subtitle2" sx={{ textAlign: "center", px: 2 }}>
                                    {typeof file === "string" ? file : file.name}
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                )}
            </DropZoneStyle>

            {fileRejections.length > 0 && <RejectionFiles fileRejections={fileRejections} />}

            {helperText && helperText}
        </Box>
    )
}
